import { ColumnSpec } from '~types/columnSpec';

import { ColumnPickerGroup } from '~components/table-view';

import { ExportColumn, ExportColumnType } from '~support-at-home/models/exportColumns';

// File order for the Statements CSV export. Presentation only: the backend's specs array is the
// source of which columns exist. A key not listed here is appended rather than dropped, so a
// newly registered column still exports without a frontend change.
const COLUMN_ORDER = [
  'client_name',
  'client_id',
  'care_recipient_id',
  'care_manager_name',
  'statement_reference',
  'statement_period',
  'funding_type',
  'opening_balance',
  'spent',
  'closing_balance',
  'co_contributions',
];

export const COLUMN_GROUPS: ColumnPickerGroup[] = [
  { title: 'Client', keys: ['client_name', 'client_id', 'care_recipient_id', 'care_manager_name'] },
  { title: 'Statement', keys: ['statement_reference', 'statement_period', 'funding_type'] },
  { title: 'Balances', keys: ['opening_balance', 'spent', 'closing_balance', 'co_contributions'] },
];

const CURRENCY_KEYS = new Set(['opening_balance', 'spent', 'closing_balance', 'co_contributions']);

export const orderColumnSpecs = (columnSpecs: ColumnSpec[]): ColumnSpec[] => {
  const specsByKey = new Map(columnSpecs.map((spec) => [spec.key, spec]));
  const ordered = COLUMN_ORDER.map((key) => specsByKey.get(key)).filter((spec): spec is ColumnSpec => Boolean(spec));
  const remaining = columnSpecs.filter((spec) => !COLUMN_ORDER.includes(spec.key));
  return [...ordered, ...remaining];
};

const exportTypeFor = (key: string): ExportColumnType => (CURRENCY_KEYS.has(key) ? 'currency' : 'text');

// ── CSV export column contract ────────────────────────────────────────────────
// The document engine renders whatever { key, label, type } list the export job carries, in
// that order. The export is long format (one row per statement and funding type), unlike the
// on-screen table, which pivots funding types into column groups, so the export columns come
// straight from the specs rather than from the table's columns.

export const exportColumnsFor = (columnSpecs: ColumnSpec[]): ExportColumn[] =>
  orderColumnSpecs(columnSpecs)
    .filter((spec) => spec.visible !== false)
    .map((spec) => ({ key: spec.key, label: spec.label, type: exportTypeFor(spec.key) }));
